import { useEffect, useState } from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useAuth } from "@/hooks/use-auth";

export type UserProfile = {
  full_name: string | null;
  avatar_url: string | null;
  email: string | null;
  role: string | null;
};

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const load = async () => {
    if (!user?.id || !isSupabaseConfigured || !supabase) {
      setProfile(null);
      return;
    }
    setLoading(true);

    const { data } = await supabase
      .from("profiles")
      .select("full_name, avatar_url, email, role")
      .eq("id", user.id)
      .maybeSingle();

    if (data) {
      setProfile(data as UserProfile);
      setLoading(false);
      return;
    }

    if (user.email) {
      await supabase
        .from("profiles")
        .upsert({ id: user.id, email: user.email }, { onConflict: "id" });

      const { data: created } = await supabase
        .from("profiles")
        .select("full_name, avatar_url, email, role")
        .eq("id", user.id)
        .maybeSingle();
      if (created) setProfile(created as UserProfile);
    }

    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [user?.id]);

  // Assinatura em tempo real para refletir mudanças no avatar/nome imediatamente
  useEffect(() => {
    if (!user?.id || !isSupabaseConfigured || !supabase) return;

    const channel = supabase
      .channel(`realtime:profiles:${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "profiles", filter: `id=eq.${user.id}` },
        (payload) => {
          const next = payload.new as Partial<UserProfile> | null;
          if (next) {
            setProfile((prev) => ({ ...(prev || { full_name: null, avatar_url: null, email: null, role: null }), ...next }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  return { profile, loading, refresh: load };
};